import React, { useState, useRef, useEffect } from 'react';
import { Text, Button } from '@cuemath/web-comps';

import {
  PickerWrap,
  PickerField,
  MSPop,
  MSBar,
  MSFooter,
  MSLink,
  MSList,
  MSOption,
  MSCheck,
  UI,
} from '../../styled-comps';

// Multi-select dropdown with an Apply button INSIDE the popover: toggling
// options edits a local draft; the change is committed to the parent (via
// onApply) only when Apply is pressed. This is a pure client-side filter — it
// never triggers a refetch. Closing without Apply (outside click / Cancel)
// discards the draft. `value`/`options` are arrays of scalar values.
const MultiSelectApply = ({
  label = 'Filter',
  options,
  value,
  onApply,
  allLabel = 'All',
  noneLabel = 'None',
  optionLabel = option => option,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || []);
  const wrapRef = useRef(null);

  // Re-sync the draft to the applied value each time the popover opens, so a
  // discarded edit never leaks into the next session.
  const openPopover = () => {
    setDraft(value || []);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = event => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggle = optionValue => {
    setDraft(prev =>
      prev.includes(optionValue)
        ? prev.filter(item => item !== optionValue)
        : [...prev, optionValue],
    );
  };

  // The chip mirrors exactly what is applied: empty means nothing renders
  // downstream, so it must read as "none" — not "all" — to match the consumer,
  // which filters by membership (an empty selection shows no rows).
  const applied = value || [];
  const summary = !applied.length
    ? noneLabel
    : applied.length === options.length
    ? allLabel
    : `${applied.length} of ${options.length} selected`;

  const handleApply = () => {
    onApply(draft);
    setOpen(false);
  };

  return (
    <PickerWrap ref={wrapRef}>
      <PickerField onClick={() => (open ? setOpen(false) : openPopover())}>
        <Text variant="body3" color={UI.ink}>{`${label}: ${summary}`}</Text>
      </PickerField>
      {open && (
        <MSPop>
          <MSBar>
            <MSLink type="button" onClick={() => setDraft(options.slice())}>
              Select all
            </MSLink>
            <MSLink type="button" onClick={() => setDraft([])}>
              Clear
            </MSLink>
          </MSBar>
          <MSList>
            {options.map(optionValue => {
              const active = draft.includes(optionValue);
              return (
                <MSOption
                  key={optionValue}
                  role="checkbox"
                  aria-checked={active}
                  tabIndex={0}
                  onClick={() => toggle(optionValue)}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggle(optionValue);
                    }
                  }}
                >
                  <MSCheck $active={active}>{active ? '✓' : ''}</MSCheck>
                  <Text variant="body3" color={UI.ink}>{optionLabel(optionValue)}</Text>
                </MSOption>
              );
            })}
          </MSList>
          <MSFooter>
            <MSLink type="button" onClick={() => setOpen(false)}>
              Cancel
            </MSLink>
            <Button size="small" label="Apply" onClick={handleApply} />
          </MSFooter>
        </MSPop>
      )}
    </PickerWrap>
  );
};

export default MultiSelectApply;
